import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight, CalendarCheck2, Clock3, XCircle, TrendingUp } from "lucide-react";
import { PageHeader } from "@/components/app/PageHeader";

const STATUS_STYLES = {
  present: "bg-primary text-primary-foreground",
  late: "bg-amber-400 text-amber-950",
  absent: "bg-destructive text-destructive-foreground",
};

function monthLabel(month, lang) {
  const [y, m] = month.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(lang === "ms" ? "ms-MY" : "en-MY", {
    month: "long",
    year: "numeric",
  });
}

function shiftMonth(month, delta) {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function ParentAttendancePage() {
  const { t, lang } = useI18n();
  const [kids, setKids] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/students").then((r) => {
      setKids(r.data.students);
      if (r.data.students.length > 0) setStudentId((prev) => prev || r.data.students[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!studentId) return;
    setLoading(true);
    api
      .get("/attendance/student/insights", { params: { student_id: studentId, month } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [studentId, month]);

  const dayMap = useMemo(() => {
    const map = {};
    (data?.days || []).forEach((d) => {
      map[Number(d.date.slice(8, 10))] = d;
    });
    return map;
  }, [data]);

  const weekDays = lang === "ms"
    ? ["Isn", "Sel", "Rab", "Kha", "Jum", "Sab", "Ahd"]
    : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const cells = useMemo(() => {
    if (!data) return [];
    const blanks = Array.from({ length: data.first_weekday }, () => null);
    const days = Array.from({ length: data.days_in_month }, (_, i) => i + 1);
    return [...blanks, ...days];
  }, [data]);

  const totals = data?.totals || { present: 0, late: 0, absent: 0 };

  return (
    <div className="space-y-6" data-testid="parent-attendance-insights">
      <PageHeader
        eyebrow="Kelas Program Membaca PMM"
        title={t("attendanceInsights")}
        subtitle={t("attendanceInsightsSub")}
      />

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={studentId} onValueChange={setStudentId}>
          <SelectTrigger className="h-11 w-56 rounded-xl" data-testid="insights-child-select">
            <SelectValue placeholder={t("selectChild")} />
          </SelectTrigger>
          <SelectContent>
            {kids.map((k) => (
              <SelectItem key={k.id} value={k.id} data-testid={`insights-child-${k.id}`}>
                {k.full_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1 rounded-xl border border-border/70 bg-background px-1 py-1">
          <Button variant="ghost" size="icon" className="h-9 w-9 rounded-lg" onClick={() => setMonth((m) => shiftMonth(m, -1))} data-testid="insights-prev-month">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="num min-w-36 text-center text-sm font-semibold">{monthLabel(month, lang)}</span>
          <Button variant="ghost" size="icon" className="h-9 w-9 rounded-lg" onClick={() => setMonth((m) => shiftMonth(m, 1))} data-testid="insights-next-month">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="pmm-pop rounded-[1.5rem] border-border/60 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" style={{ animationDelay: "0.05s" }}><CardContent className="p-5">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground"><CalendarCheck2 className="h-4 w-4 text-green-600" /> {t("presentDays")}</div>
          <div className="num mt-2 font-display text-4xl font-extrabold text-green-600" data-testid="insights-present">{totals.present}</div>
        </CardContent></Card>
        <Card className="pmm-pop rounded-[1.5rem] border-border/60 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" style={{ animationDelay: "0.11s" }}><CardContent className="p-5">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground"><Clock3 className="h-4 w-4 text-amber-500" /> {t("lateDays")}</div>
          <div className="num mt-2 font-display text-4xl font-extrabold text-amber-500" data-testid="insights-late">{totals.late}</div>
        </CardContent></Card>
        <Card className="pmm-pop rounded-[1.5rem] border-border/60 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" style={{ animationDelay: "0.17s" }}><CardContent className="p-5">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground"><XCircle className="h-4 w-4 text-destructive" /> {t("absentDays")}</div>
          <div className="num mt-2 font-display text-4xl font-extrabold text-destructive" data-testid="insights-absent">{totals.absent}</div>
        </CardContent></Card>
        <Card className="pmm-pop rounded-[1.5rem] border-border/60 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" style={{ animationDelay: "0.23s" }}><CardContent className="p-5">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground"><TrendingUp className="h-4 w-4 text-orange-500" /> {t("attendanceRate")}</div>
          <div className="num mt-2 font-display text-4xl font-extrabold text-blue-950" data-testid="insights-rate">{data?.attendance_rate ?? 0}%</div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-gradient-to-r from-orange-600 to-yellow-400 transition-[width] duration-500" style={{ width: `${data?.attendance_rate ?? 0}%` }} />
          </div>
        </CardContent></Card>
      </div>

      {/* Calendar chart */}
      <Card className="pmm-pop rounded-[1.5rem] border-border/60 shadow-lg" style={{ animationDelay: "0.28s" }}>
        <CardContent className="p-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-display text-sm font-extrabold uppercase tracking-widest text-blue-950">
              {data?.student_name || ""} <span className="text-xs font-semibold normal-case tracking-normal text-muted-foreground">{data?.level ? `· ${data.level}` : ""}</span>
            </h2>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-md bg-primary" /> {t("present")}</span>
              <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-md bg-amber-400" /> {t("late")}</span>
              <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-md bg-destructive" /> {t("absent")}</span>
            </div>
          </div>

          {loading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">…</p>
          ) : (
            <>
              <div className="grid grid-cols-7 gap-1.5 text-center">
                {weekDays.map((d) => (
                  <div key={d} className="pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{d}</div>
                ))}
                {cells.map((day, i) => {
                  if (day === null) return <div key={`b-${i}`} />;
                  const rec = dayMap[day];
                  return (
                    <div
                      key={day}
                      title={rec ? `${rec.date}: ${rec.status}` : undefined}
                      data-testid={rec ? `insights-day-${day}` : undefined}
                      className={`num grid aspect-square place-items-center rounded-xl text-sm font-medium transition-transform ${
                        rec ? `${STATUS_STYLES[rec.status]} scale-100 shadow-sm` : "bg-muted/50 text-muted-foreground/60"
                      }`}
                    >
                      {day}
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                <span className="num">{data?.marked_days ?? 0} {t("markedDays")}</span>
                {(data?.marked_days ?? 0) === 0 && <span>{t("noAttendanceData")}</span>}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
